import React from "react";
import { Activity, ArrowRight, ArrowUpRight, Calendar, CheckCircle2, Terminal } from "lucide-react";
import { Link } from "react-router-dom";

const journey = [
  {
    year: "2018 – 2022",
    title: "BE in IT",
    category: "Academic",
    details: ["Specialized in software, full-stack web, and cloud.", "Developed strong coding & debugging skills."],
  },
  {
    year: "2021",
    title: "Full-Stack Certification",
    category: "Specialization",
    details: ["Built real-world React + Node.js projects.", "Gained deployment & CI/CD experience."],
  },
  {
    year: "2022 – 2025",
    title: "Godrej & Boyce",
    category: "Enterprise",
    details: ["Led enterprise IT + NDT platform development.", "Optimized workflows with dashboards."],
  },
  {
    year: "2024",
    title: "PCN UT & PAUT Level II",
    category: "Authority",
    details: ["Certified advanced NDT inspector.", "Ensured compliance & trained juniors."],
  },
  {
    year: "2025 – Present",
    title: "Focus Group",
    category: "Foundership",
    details: ["Developing SaaS platforms & training portals.", "Scaling solutions for global clients."],
  },
  {
    year: "Future",
    title: "Upcoming Projects",
    category: "Growth",
    details: ["Expanding automated NDT software ecosystems."],
  },
];

export default function JourneyGrid() {
  return (
<div className="bg-slate-50 min-h-screen pb-20">
      
      {/* 1. HERO HEADER - Shared Design Pattern */}
      <section className="bg-white border-b border-slate-200 pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-between lg:items-end gap-8">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 md:w-10 h-[1px] bg-cyan-600" />
                <span className="font-mono text-[10px] md:text-xs tracking-[0.4em] uppercase font-black text-slate-400">
                  Operational History
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-slate-950 tracking-tighter leading-tight">
                Professional <span className="text-cyan-600 font-light italic">Evolution</span>
              </h1>
            </div>
            
            {/* Category Indicator - Non-interactive or interactive filter */}
           
          </div>
        </div>
      </section>

      {/* 2. EVALUATION GRID - Matching Ventures Two-Column Layout */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-12 md:mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {journey.map((item, idx) => (
            <div key={idx} className="space-y-6 md:space-y-8 group">
              
              {/* Step Header */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm group-hover:border-cyan-500/50 transition-all duration-500">
                  <span className="font-mono text-lg font-black text-slate-950 group-hover:text-cyan-600">
                    0{idx + 1}
                  </span>
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-slate-950 group-hover:text-cyan-600 transition-colors">
                    {item.title}
                  </h2>
                  <p className="font-mono text-[10px] font-black text-cyan-600 uppercase tracking-widest">
                    {item.year} // {item.category}
                  </p>
                </div>
              </div>

              {/* Technical Profile Card */}
              <div className="bg-white border border-slate-200 rounded-[2rem] md:rounded-3xl p-6 md:p-8 shadow-sm group-hover:border-cyan-200 group-hover:shadow-md transition-all">
                <div className="flex justify-between items-start mb-6">
                   <h3 className="font-mono text-[9px] md:text-[10px] font-black uppercase tracking-widest text-cyan-600">
                    Evaluation_Parameters
                  </h3>
                  <Terminal size={16} className="text-slate-300 group-hover:text-cyan-500/30" />
                </div>

                {/* Details List */}
                <div className="space-y-4 mb-8">
                  {item.details.map((d, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5 shrink-0" />
                      <span className="text-slate-600 text-sm md:text-base leading-snug">
                        {d}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Footer Decor */}
                <div className="flex items-center justify-between pt-6 border-t border-slate-50">
                  
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-100 group-hover:bg-cyan-500 transition-colors" />
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-100 group-hover:bg-cyan-500/50 transition-colors" />
                  </div>
                </div>
              </div>

              {/* Skill Tags */}
              <div className="flex flex-wrap gap-2">
                {item.tags?.map((tag, tIdx) => (
                  <span key={tIdx} className="px-3 md:px-4 py-1.5 md:py-2 bg-slate-200/50 text-slate-700 rounded-lg text-[10px] md:text-xs font-bold uppercase tracking-wide">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* 3. FINAL SUMMARY BLOCK */}
        <div className="mt-20 md:mt-24 bg-slate-950 rounded-[2rem] md:rounded-[3.5rem] p-8 md:p-16 relative overflow-hidden shadow-2xl shadow-slate-300">
          <div className="relative z-10 flex flex-col lg:flex-row justify-between items-center gap-12">
            <div className="max-w-2xl text-center lg:text-left">
              <h4 className="text-3xl md:text-4xl font-black text-white mb-6 uppercase tracking-tight leading-none">
                Synthesizing <span className="text-cyan-500">Dual Systems</span>
              </h4>
              <p className="text-slate-400 text-lg md:text-xl leading-relaxed font-medium italic border-l-4 border-cyan-600 pl-6 mb-0">
                "I combine high-fidelity physical testing with scalable software systems to eliminate industrial downtime."
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
              <Link
                to="/projects"
                className="flex items-center justify-center gap-4 bg-white text-slate-950 px-10 py-5 rounded-2xl font-black text-xs uppercase tracking-[0.2em] hover:bg-cyan-500 hover:text-white transition-all active:scale-95"
              >
                Projects <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}